import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Loader, Center, Stack, Text } from '@mantine/core';

/**
 * ProtectedRoute - Route guard for authenticated users only, whatever their role
 *
 * Redirects to /login if user is not authenticated, carrying the requested path as
 * `returnTo` so LoginPage can send the user back after sign-in.
 */
export const ProtectedRoute: React.FC = (): React.ReactElement => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text size="sm" c="dimmed">
            Loading...
          </Text>
        </Stack>
      </Center>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    const returnTo = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/login?returnTo=${returnTo}`} replace />;
  }

  // Render child routes
  return <Outlet />;
};
